<template>
    <div :class="['text-area-container',  {'dark': darkScheme}]">
        <div :class="['ufo-textarea', {'disabled' : disabled, 'error': hasError, 'focus': focused, 'has-char-limit-inside': charLimitPosition === 'inside'}]">
            <label v-if="label" class="inside-label" :class="{'focus' : focused || disabled || modelValue}">{{ label }}</label>
            <textarea
                :class="{'disabled' : disabled}"
                :value="modelValue"
                :name="name"
                :rows="rows"
                :maxlength="maxLength"
                :placeholder="(focused || !label) ? placeholder : label"
                :disabled="disabled"
                @input="handleInput"
                @focus="handleFocus"
                @blur="blur()"
                @keyup="handleKeyup"
                @keydown="handleKeydown"
                @paste="handlePaste"
            ></textarea>
            <div v-if="showCharLimit" :class="['char-limit-message', charLimitPosition, { 'limit-reached': currentLength >= maxLength }]">
                {{ currentLength }} / {{ maxLength }}
            </div>
        </div>
        <div v-if="hasAlertSlot" class="alert-message">
            <slot name="alert"></slot>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'text-area',
        inheritAttrs: false,
        props: {
            label: {
                type: String,
                default: null
            },
            name: {
                type: String,
                default: null
            },
            modelValue: {
                type: String,
                default: null
            },
            placeholder: {
                type: String,
                default: null
            },
            rows: {
                type: Number,
                default: null
            },
            maxLength: {
                type: Number,
                default: null
            },
            disabled: {
                type: Boolean,
                default: false
            },
            hasError: {
                type: String,
                default: null
            },
            disableEnter: {
                type: Boolean,
                default:true
            },
            darkScheme: {
                type: Boolean,
                default: false
            },
            charLimitPosition: {
                type: String,
                default: null // inside | outside
            }
        },
        data() {
            return {
                focused: false,
                affirmed: false,
            }
        },
        computed: {
            hasAlertSlot() {
                return !!this.$slots['alert']
            },
            currentLength() {
                return String(this.modelValue ?? '').length;
            },
            showCharLimit() {
                if (!this.charLimitPosition || !this.maxLength) {
                    return false;
                }

                return this.currentLength >= Math.max(Math.ceil(this.maxLength * 0.8), this.maxLength - 10);
            }
        },
        methods: {
            handleInput(e) {
                this.$emit('update:modelValue', e.target.value);
            },
            handleKeyup() {
                this.$emit('keyup');
            },
            handleKeydown(e) {
                this.$emit('keydown', e);
            },
            handleFocus() {
                this.focused = true;
                this.$emit('focus');
            },
            blur() {
                this.focused = false;
                this.affirmed = false;
                this.$emit('blur');
            },
            click() {
                this.$emit('click');
            },
            handlePaste() {
                this.$emit('paste')
            }
        }
    }
</script>

<style lang="scss" scoped>
.text-area-container {
    --bgColor: var(--asphalt10);
    --disabledBgColor: var(--asphalt10);

    --outlineColor: var(--asphalt30);
    --hoverOutlineColor: var(--asphalt50);
    --focusOutlineColor: var(--asphalt100);
    --disabledOutlineColor: var(--asphalt20);

    --labelColor: var(--asphalt50);
    --disabledLabelColor: var(--asphalt40);

    --textColor: var(--asphalt100);

    --warningColor: var(--error50);
    --successColor: var(--success60);

    &.dark {
        --bgColor: var(--asphalt95);
        --disabledBgColor: var(--asphalt90);

        --outlineColor: var(--asphalt70);
        --hoverOutlineColor: var(--asphalt50);
        --focusOutlineColor: var(--asphalt10);
        --disabledOutlineColor: var(--asphalt80);

        --labelColor: var(--asphalt40);
        --disabledLabelColor: var(--asphalt50);

        --textColor: var(--asphalt10);

        --warningColor: var(--error50);
        --successColor: var(--success60);
    }
    .ufo-textarea {


        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        position: relative;

        font: var(--editor-ui-b2-regular);

        background-color: var(--bgColor);
        outline: solid 1px var(--outlineColor);
        border-radius: 4px;
        color: var(--textColor);

        &.focus {
            outline: 1px solid var(--focusOutlineColor);
        }

        &:hover:not(.focus) {
            outline: 1px solid var(--hoverOutlineColor);
        }

        &.error {
            outline: 1px solid var(--warningColor);
        }

        &.disabled {
            cursor: not-allowed;
            background: var(--disabledBgColor);
        }

        .disabled {
            cursor: not-allowed;
            background: var(--disabledBgColor);
            color: var(--labelColor);
        }
    }

    label {
        line-height: 1;
        display: none;
        color: var(--labelColor);
        margin-bottom: 0;

        &.focus {
            font: var(--editor-ui-b3-regular);
            position: absolute;
            display: block;
            left: 12px;
            top: -10px;
            padding: 0 4px;
            background: var(--bgColor);
        }
    }

    textarea {
        font: var(--editor-ui-b2-regular);
        background-color: var(--bgColor);
        outline: none;
        border-radius: 4px;
        color: var(--textColor);
        padding: 14px 16px;
        height: 100%;

        &::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
            color: var(--labelColor);
            opacity: 1; /* Firefox */
        }

        &:-ms-input-placeholder { /* Internet Explorer 10-11 */
            color: var(--labelColor);
        }

        &::-ms-input-placeholder { /* Microsoft Edge */
            color: var(--labelColor);
        }
    }

    .ufo-textarea.has-char-limit-inside textarea {
        padding-bottom: 28px;
    }

    .alert-message {
        font: var(--editor-ui-b2-regular);
        display: flex;
        align-items: center;
        margin-top: 8px;
        color: var(--warningColor);
    }

    .char-limit-message {
        font: var(--editor-ui-b2-regular);
        display: flex;
        align-items: center;
        color: var(--asphalt40);
        opacity: 0;
        transition: opacity 0.15s ease;

        &.inside {
            position: absolute;
            right: 8px;
            bottom: 4px;
            z-index: 1;
            pointer-events: none;
        }

        &.outside {
            position: absolute;
            right: -2px;
            bottom: -36px;
            z-index: 1;
            pointer-events: none;
            background: var(--fabBackgroundColor);
            color: var(--fabColor);
            padding: 4px 8px;
            border-radius: 4px;
        }

        &.limit-reached {
            color: var(--error50);

            &.outside {
                background: var(--fabAlertColor);
                color: var(--fabColor);
        }
    }

    .text-area-container:focus-within .char-limit-message {
        opacity: 1;
    }
}

    .text-area-container {
        padding-bottom: 23px;
        width: 100%;
        height: 100%;

        &:focus {
            //color: var(--textColor);
            outline: var(--focusOutlineColor);
        }

        &:active {
            //color: var(--carbon70);
        }
    }

}
</style>